import { useEffect } from "react";

import ShippingBox from "atoms/icon/icons/shipping_box.svg";
import { ImagePreloader } from "atoms/image-preloader/ImagePreloader";
import { DescriptiveTag } from "atoms/tag/DescriptiveTag";
import { JustForYouTag } from "atoms/tag/JustForYouTag";
import { useIntersectionObserver } from "hooks/useIntersectionObserver";
import { usePrefetchPDP } from "hooks/usePrefetchPDP";
import { getPricingText } from "services/ListingHelperService";
import { formatCurrency } from "toolbox/Money";

interface ProductCardSmallProps {
  title: string;
  description?: string | null;
  imageUrl: string[];
  type:
    | "Treatment"
    | "Package"
    | "Membership"
    | "Product"
    | "ProductFromExternalSource";
  tag?: string | null;
  featuredTag?: string | null;
  concerns?: Array<string> | null;
  price: number;
  memberPrice?: number | null;
  unitType?: string | null;
  hasLinearPricing?: boolean;
  guestMode?: boolean;
  discount?: string | null | undefined;
  customColor?: string | null;
  onClick?: () => void;
  isRecommended?: boolean;
  /** Optional: ID of the item to enable prefetching */
  itemId?: string;
}

export const ProductCardSmall = ({
  title,
  imageUrl,
  type,
  tag,
  featuredTag,
  price,
  memberPrice,
  unitType,
  hasLinearPricing = false,
  guestMode,
  onClick,
  discount,
  customColor,
  isRecommended = false,
  itemId,
}: ProductCardSmallProps) => {
  const [ref, isIntersecting] = useIntersectionObserver<HTMLDivElement>({
    rootMargin: "200px", // Start prefetching 200px before the card enters viewport
    threshold: 0,
    triggerOnce: true, // Only prefetch once
  });

  const { prefetchPackagePDP, prefetchMembershipPDP, prefetchProductPDP } =
    usePrefetchPDP();

  useEffect(() => {
    if (!isIntersecting || !itemId) return;

    // Prefetch the appropriate PDP data based on the card type
    switch (type) {
      case "Treatment":
      case "Package":
        prefetchPackagePDP(itemId);
        break;
      case "Membership":
        prefetchMembershipPDP(itemId);
        break;
      case "Product":
      case "ProductFromExternalSource":
        prefetchProductPDP(itemId);
        break;
    }
  }, [
    isIntersecting,
    itemId,
    type,
    prefetchPackagePDP,
    prefetchMembershipPDP,
    prefetchProductPDP,
  ]);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onClick?.();
    }
  };

  return (
    <div
      ref={ref}
      className="flex w-full cursor-pointer flex-col overflow-hidden rounded-lg bg-white shadow-sm backdrop-blur-sm transition-shadow active:scale-[0.98]"
      onClick={onClick}
      onKeyDown={handleKeyDown}
    >
      {imageUrl && imageUrl.length > 0 && (
        <div className="relative">
          {/* Discount Pill - Top Left over Image */}
          {discount && (
            <div className="absolute left-2 top-2 z-10">
              <DescriptiveTag
                color={customColor ? "custom" : "primary"}
                size="small"
                customColor={customColor}
              >
                {discount}
              </DescriptiveTag>
            </div>
          )}
          <ImagePreloader
            url={imageUrl[0]}
            lazy={true}
            fade={150}
            preloadDistance="100px"
            className="w-full"
            placeholder={
              <div className="h-[200px] w-full animate-pulse rounded-t-lg bg-gray-100" />
            }
          >
            {type === "ProductFromExternalSource" && (
              <div className="absolute right-2 top-2 rounded-full bg-brand-tint p-1">
                <ShippingBox className="h-4 w-4 text-brand-color" />
              </div>
            )}
            <img
              className="h-[200px] w-full rounded-t-lg object-cover"
              src={imageUrl[0]}
              alt={title}
            />
          </ImagePreloader>
          {/* Transparent gradient fade from image to card */}
          <div className="absolute bottom-0 h-8 w-full bg-gradient-to-b from-transparent to-white/80" />
        </div>
      )}

      <div className="flex flex-1 flex-col px-3 pb-3 pt-2">
        {isRecommended && (
          <div className="mb-2 flex items-start">
            <JustForYouTag />
          </div>
        )}
        <div className="mb-2 flex min-h-[20px] flex-wrap gap-1">
          {featuredTag && (
            <DescriptiveTag color="primary" size="small">
              {featuredTag}
            </DescriptiveTag>
          )}
          {tag && (
            <DescriptiveTag color="secondary" size="small">
              {tag}
            </DescriptiveTag>
          )}
        </div>

        <div className="text-h4 line-clamp-2 break-words text-left font-serif leading-tight tracking-tight text-primary">
          {title}
        </div>
      </div>

      <div className="mt-auto flex w-full items-center bg-one px-3 py-2">
        {guestMode && (
          <span className="text-body2 text-primary">Sign up to see price</span>
        )}
        {!guestMode && (
          <div className="flex items-center gap-1.5">
            <span
              className={`text-body2 text-primary ${
                memberPrice != null && memberPrice !== price
                  ? ""
                  : "font-semibold"
              }`}
            >
              {getPricingText(hasLinearPricing, price, unitType, type)}
            </span>
            {memberPrice != null && memberPrice !== price && (
              <>
                <span className="text-body2 text-neutral-300">|</span>
                <span className="text-body2 font-semibold text-primary">
                  {formatCurrency(memberPrice)}{" "}
                  <span className="font-normal">Member</span>
                </span>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
